import { ChangeDetectorRef, Directive, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, AsyncValidator, NG_ASYNC_VALIDATORS, ValidationErrors } from '@angular/forms';
import { DfEventService } from '@df/core/service/df-event';
import { TimeoutService } from '@df/ng/timeout.service';
import { NDfModuleLoqate } from '../df-module-loqate.config';
import { LoqatePhoneValidationEvent, LoqatePhoneValidatorService } from './loqate-phone-validator.service';

declare module '@df/core/service/df-event' {
    interface IEventType {
        [NDfModuleLoqate.Events.PhoneValidation]: DfEventConfig<
            NDfModuleLoqate.Events.PhoneValidation,
            {
                value: string | undefined | null;
                countryCode?: string;
            },
            void
        >;
    }
}

/**
 * A directive that adds the `loqate` validator to controls marked with the
 * `phone` attribute, via the `NG_ASYNC_VALIDATORS` provider.
 *
 * @example
 * ```html
 * <input type="text" loqatePhone="GB" [(ngModel)]="phone" />
 * ```
 */
@Directive({
    selector: '[loqatePhone][ngModel],[loqatePhone][formControl],[loqatePhone][formControlName]',
    standalone: true,
    providers: [
        {
            provide: NG_ASYNC_VALIDATORS,
            useExisting: LoqatePhoneValidateDirective,
            multi: true
        }
    ]
})
export class LoqatePhoneValidateDirective implements AsyncValidator, OnChanges, OnDestroy {
    /**
     * The country code to use for validation.
     */
    @Input()
    loqatePhone: string | boolean | null = null;

    @Input()
    loqatePhoneDebounceTime = 0;

    @Output()
    loqatePhoneResponse = new EventEmitter<LoqatePhoneValidationEvent>();

    /**
     * A flag that tracks whether this validator is enabled.
     */
    _enabled?: boolean;

    protected loqatePhoneValidatorService = inject(LoqatePhoneValidatorService);
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected dfEventService = inject(DfEventService);
    protected timeoutService = inject(TimeoutService);

    private _onChange!: () => void;
    private _timeout?: number;

    ngOnChanges(changes: SimpleChanges) {
        if (changes['loqatePhone']) {
            const input = changes['loqatePhone'].currentValue;
            this._enabled = this.enabled(input);

            if (this._onChange) {
                this._onChange();
            }
        }
    }

    ngOnDestroy() {
        if (this._timeout) {
            clearTimeout(this._timeout);
            this._timeout = undefined;
        }
    }

    validate(control: AbstractControl): Promise<ValidationErrors | null> {
        const countryCode = typeof this.loqatePhone === 'string' ? this.loqatePhone : undefined;

        if (!this._enabled) {
            return Promise.resolve(null);
        }

        if (this._timeout) {
            clearTimeout(this._timeout);
        }

        return new Promise(resolve => {
            this._timeout = this.timeoutService.setTimeout(async () => {
                const result = await this.loqatePhoneValidatorService.validate(control, countryCode, this.loqatePhoneResponse);

                this.dfEventService.dispatch({
                    id: NDfModuleLoqate.Events.PhoneValidation,
                    data: {
                        value: control.value,
                        countryCode
                    }
                });

                resolve(result);
            }, this.loqatePhoneDebounceTime);
        });
    }

    /**
     * Registers a callback function to call when the validator inputs change.
     */
    registerOnValidatorChange(fn: () => void): void {
        this._onChange = fn;
    }

    enabled(input: boolean | string | null): boolean {
        if (typeof input === 'boolean') {
            return input;
        }

        if (typeof input === 'string') {
            return input !== 'false';
        }

        return true;
    }
}
